import { ArrowForward, StarBorderOutlined, StarOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, CardHeader, IconButton, Typography } from "@mui/material";
import { PropsWithChildren, ReactElement } from "react";
import { useFavorite } from "@arelia-hooks/useFavorite";
import SpinnerWrapper from "./SpinnerWrapper";
import { CONSTANTS } from "@arelia-utils/constants/constants";
import { Link, useNavigate } from "react-router-dom";
import EmptyState from "./EmptyState";
import { Widget, WidgetHeight } from "@arelia-types/widget";

type Props = {
  widgetId: string;
  title: string;
  loading?: boolean;
  error?: boolean;
  unconnected?: boolean;
  footerText?: string;
  navigateLink?: string;
  headerComponent?: JSX.Element;
  extraActions?: ReactElement;
  widgetConfig?: Widget;
  disableFavorite?: boolean;
}

export default function WidgetCard({
  widgetId,
  title,
  loading = false,
  error = false,
  unconnected = false,
  footerText,
  navigateLink,
  headerComponent,
  children,
  extraActions,
  widgetConfig,
  disableFavorite = false
}: PropsWithChildren<Props>) {
  const [isFavorite, toggleFavorite] = useFavorite(widgetId);
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (navigateLink && !unconnected) {
      navigate(navigateLink);
    }
  }

  const handleFavoriteToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    toggleFavorite();
  }

  const handleHeaderEvent = (event: React.MouseEvent) => {
    event.stopPropagation();
  }

  const renderContent = () => {
    if (error) return (
      <Typography variant="body2" color="error">
        {CONSTANTS.UI_MESSAGES.GET_FETCH_ERROR}
      </Typography>
    );

    if (unconnected) return (
      <EmptyState minified={widgetConfig?.height === WidgetHeight.S} />
    );

    return children;
  }

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }} onClick={handleNavigate} className={`${navigateLink && !unconnected ? "highlighted" : ""}`}>
      <CardHeader title={
        <>
          <Box>{title}</Box>
          {!error && headerComponent && (
            <div onClick={handleHeaderEvent}>
              {headerComponent}
            </div>
          )}
        </>
      } action={
        <>
          {!disableFavorite && <IconButton onClick={handleFavoriteToggle}>
            {isFavorite ? <StarOutlined /> : <StarBorderOutlined />}
          </IconButton>}
          {extraActions}
        </>
      } />
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <SpinnerWrapper loading={loading}>
          <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
            {renderContent()}
          </Box>
          {(footerText || navigateLink) && !unconnected &&
            <Box display="flex" justifyContent="space-between" alignItems="end">
              <Typography variant="body2" color="secondary">{footerText}</Typography>
              {navigateLink &&
                <IconButton to={navigateLink} component={Link} sx={{ mb: "-8px", mr: "-8px" }}>
                  <ArrowForward color="primary" sx={{ ...CONSTANTS.ICON_SX }} />
                </IconButton>}
            </Box>}
        </SpinnerWrapper>
      </CardContent>
    </Card>
  )
}